import React, { useEffect, useRef, useState } from 'react';

// Swipe horizontally (touch devices / PWA) to jump to the sibling admin app.
// Mirrored in the cncx repo — keep the two copies in sync.
//
// A deliberate horizontal drag shows a pill with the target app's name; dragging
// past the threshold and releasing navigates there. Swipes that start inside
// horizontally-scrollable content (tables, carousels) or form fields are ignored
// so normal scrolling and typing keep working, and mostly-vertical gestures
// (page scrolls) never trigger it.
export default function AppSwitchSwipe({ targetUrl, targetName }) {
  const [drag, setDrag] = useState(null); // { dir: 'left'|'right', progress: 0..1 }
  const [leaving, setLeaving] = useState(false);
  const stateRef = useRef(null);

  useEffect(() => {
    const ENGAGE_DX = 32; // px of horizontal movement before the gesture arms
    const threshold = () => Math.min(170, window.innerWidth * 0.4);

    const isFormEl = (el) =>
      !!(el && el.closest && el.closest('input, textarea, select, [contenteditable="true"]'));

    // Any ancestor that actually scrolls horizontally owns horizontal drags.
    const inHorizontalScroller = (el) => {
      for (let n = el; n && n !== document.body; n = n.parentElement) {
        if (n.scrollWidth > n.clientWidth + 4) {
          const ox = window.getComputedStyle(n).overflowX;
          if (ox === 'auto' || ox === 'scroll') return true;
        }
      }
      return false;
    };

    const onStart = (e) => {
      if (e.touches.length !== 1) { stateRef.current = null; return; }
      const t = e.touches[0];
      if (isFormEl(e.target) || inHorizontalScroller(e.target)) { stateRef.current = null; return; }
      stateRef.current = { x0: t.clientX, y0: t.clientY, dx: 0, dy: 0, engaged: false };
    };

    const onMove = (e) => {
      const s = stateRef.current;
      if (!s || e.touches.length !== 1) return;
      const t = e.touches[0];
      s.dx = t.clientX - s.x0;
      s.dy = t.clientY - s.y0;
      if (!s.engaged) {
        // Clearly a vertical scroll — stand down for the rest of this touch.
        if (Math.abs(s.dy) > 40 && Math.abs(s.dy) > Math.abs(s.dx)) {
          stateRef.current = null;
          setDrag(null);
          return;
        }
        if (Math.abs(s.dx) > ENGAGE_DX && Math.abs(s.dx) > 1.8 * Math.abs(s.dy)) s.engaged = true;
      }
      if (s.engaged) {
        setDrag({
          dir: s.dx < 0 ? 'left' : 'right',
          progress: Math.min(1, Math.abs(s.dx) / threshold()),
        });
      }
    };

    const onEnd = () => {
      const s = stateRef.current;
      stateRef.current = null;
      if (s && s.engaged && Math.abs(s.dx) >= threshold() && Math.abs(s.dx) > 2 * Math.abs(s.dy)) {
        setDrag(null);
        setLeaving(true);
        setTimeout(() => { window.location.href = targetUrl; }, 200);
      } else {
        setDrag(null);
      }
    };

    const onCancel = () => { stateRef.current = null; setDrag(null); };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    document.addEventListener('touchcancel', onCancel, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onCancel);
    };
  }, [targetUrl]);

  const ready = drag && drag.progress >= 1;

  return (
    <>
      {drag && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            [drag.dir === 'left' ? 'right' : 'left']: 12,
            transform: `translateY(-50%) scale(${0.85 + 0.15 * drag.progress})`,
            zIndex: 99999,
            pointerEvents: 'none',
            background: ready ? 'var(--orange)' : 'var(--navy)',
            color: '#fff',
            padding: '10px 16px',
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 700,
            boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
            opacity: 0.55 + 0.45 * drag.progress,
            whiteSpace: 'nowrap',
          }}
        >
          {ready ? `Release for ${targetName}` : (drag.dir === 'left' ? `${targetName} →` : `← ${targetName}`)}
        </div>
      )}
      {leaving && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100000,
            background: 'rgba(255,255,255,0.96)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            color: 'var(--navy)',
            fontSize: 16,
          }}
        >
          Opening {targetName}…
        </div>
      )}
    </>
  );
}
